<template>
    <div class="pd-profile-section">
        <p class="has-text-white mb-6">Your career details drive the salary-schedule and renewal tracking on your dashboard. Keep them current here.</p>

        <!-- Renewal summary (read-only, from the dashboard feed) -->
        <ui-card class="mb-6" v-if="pd">
            <div class="summary-head">
                <div class="has-text-weight-bold is-size-5"><i class="fad fa-graduation-cap has-text-primary mr-2"></i>Renewal Progress</div>
                <router-link :to="{name: 'dashboard'}" class="is-size-7">Dashboard <i class="fas fa-arrow-right ml-1"></i></router-link>
            </div>
            <div class="stat-strip mt-4">
                <div class="stat">
                    <div class="stat-value">{{ pd.hours.earned_total | hours }}</div>
                    <div class="stat-label">{{ unitTitle }} earned with us</div>
                </div>
                <div class="stat" v-if="pd.profile.declared">
                    <div class="stat-value">{{ pd.hours.counted | hours }}</div>
                    <div class="stat-label">earned since {{ pd.profile.window_start_on | dateOnly }}</div>
                </div>
                <div class="stat" v-if="pd.profile.declared">
                    <div class="stat-value">{{ pd.hours.remaining_to_target | hours }}</div>
                    <div class="stat-label">still needed by {{ pd.profile.renewal_on | dateOnly }}</div>
                </div>
                <div class="stat" v-if="pd.hours.in_progress.potential_hours">
                    <div class="stat-value">{{ pd.hours.in_progress.potential_hours | hours }}</div>
                    <div class="stat-label">in progress across {{ pd.hours.in_progress.count }} {{ pd.hours.in_progress.count === 1 ? 'course' : 'courses' }}</div>
                </div>
            </div>
            <p class="has-text-grey is-size-7 mt-4" v-if="!pd.profile.declared">Add your certificate renewal date below and this will track hours earned inside your renewal window.</p>
        </ui-card>

        <ui-card>
            <div class="has-text-weight-bold is-size-5 mb-4"><i class="fad fa-briefcase has-text-primary mr-2"></i>Career Profile</div>

            <div class="columns">
                <div class="column">
                    <b-field label="District" label-position="inside">
                        <b-select v-model="district_id" expanded :loading="!districts.length">
                            <option :value="0">Not Listed/Not Applicable</option>
                            <option :value="999999">Private School</option>
                            <option v-for="district in districts" v-if="district.id !== 9999999" :key="district.id" :value="district.id">{{ district.name }} ({{ district.city | capitalize }})</option>
                        </b-select>
                    </b-field>
                </div>
                <div class="column">
                    <b-field label="School" label-position="inside">
                        <b-select v-model="school_id" expanded>
                            <option :value="0">Not Listed/Not Applicable</option>
                            <option v-for="school in schools" :key="school.id" :value="school.id">{{ school.name }} ({{ school.city | capitalize }})</option>
                        </b-select>
                    </b-field>
                </div>
            </div>

            <div class="columns is-multiline">
                <div class="column is-4">
                    <b-field label="Years teaching" label-position="inside">
                        <b-select v-model="meta.years_teaching" placeholder="Select..." expanded>
                            <option v-for="option in yearsOptions" :key="option" :value="option">{{ option }}</option>
                        </b-select>
                    </b-field>
                </div>
                <div class="column is-4">
                    <b-field label="Highest degree" label-position="inside">
                        <b-select v-model="meta.highest_degree" placeholder="Select..." expanded>
                            <option v-for="option in degreeOptions" :key="option" :value="option">{{ option }}</option>
                        </b-select>
                    </b-field>
                </div>
                <div class="column is-4">
                    <b-field label="Clock hour column" label-position="inside">
                        <b-select v-model="meta.hours_column" placeholder="Select..." expanded>
                            <option v-for="option in hoursOptions" :key="option" :value="option">{{ option }}</option>
                        </b-select>
                    </b-field>
                </div>
            </div>
            <p class="has-text-grey is-size-7">Your degree and current column place you on your district's salary schedule so we can show what the next column is worth.</p>

            <hr class="my-5"/>

            <div class="has-text-weight-bold is-size-5 mb-4"><i class="fad fa-calendar-check has-text-primary mr-2"></i>Certificate Renewal</div>
            <!-- Top labels: inside/on-border labels collide with the compact
                 number-input controls in this narrower column -->
            <pd-renewal-fields v-model="meta" label-position=""/>
            <p class="has-text-grey is-size-7 mt-2">Your OSPI certificate number lives under <a href @click.prevent="$emit('go', 'certs')">Certifications &amp; Organizations</a>.</p>

            <template #footer>
                <div class="has-text-right">
                    <b-button type="is-primary" size="is-small" :loading="processing" :disabled="!loaded" @click="save"><i class="fas fa-floppy-disk mr-2"></i>Save Changes</b-button>
                </div>
            </template>
        </ui-card>
    </div>
</template>

<script>
    import {mapGetters, mapMutations} from "vuex";
    import UiCard from "../UiCard";
    import PdRenewalFields from "../fields/PdRenewalFields";
    import {client as http} from "../../http_client";
    import {YEARS_TEACHING_OPTIONS, DEGREE_OPTIONS, HOURS_COLUMN_OPTIONS} from "../../constants/profile";

    /**
     * Your Account > Professional Development: the career profile (district,
     * school, years, degree, column) and certificate-renewal settings that
     * the dashboard's professional-development section and the bundle
     * builder read. Reads through GET app/users/profile-verification (same
     * shape the modal uses) and saves through PUT app/users/pd-profile, which
     * deliberately does NOT count as a profile verification.
     */
    export default {
        name: "PdProfileSection",
        components: {UiCard, PdRenewalFields},
        filters: {
            hours(v) {
                const n = parseFloat(v) || 0;
                return Number.isInteger(n) ? String(n) : n.toFixed(1);
            }
        },
        data() {
            return {
                loaded: false,
                processing: false,
                profile: {},
                meta: {years_teaching: null, highest_degree: null, hours_column: null, pd_renewal_on: null, pd_external_hours: null, pd_target_hours: null},
                districts: [],
                schools: [],
                district_id: undefined,
                school_id: undefined,
                initialSchoolId: null,
                yearsOptions: YEARS_TEACHING_OPTIONS,
                degreeOptions: DEGREE_OPTIONS,
                hoursOptions: HOURS_COLUMN_OPTIONS,
            };
        },
        computed: {
            ...mapGetters(['currentUser', 'dashboardPd']),
            pd() {
                return this.dashboardPd;
            },
            unitTitle() {
                return (this.pd && this.pd.certification && this.pd.certification.unit_title) || 'Clock hours';
            }
        },
        methods: {
            ...mapMutations(['setUser']),
            load() {
                http.get('app/users/profile-verification', {force: true}).then(response => {
                    this.profile = {...response.data.profile};
                    this.meta = {...this.meta, ...response.data.meta};
                    this.initialSchoolId = this.profile.school_id;
                    return http.get('districts');
                }).then(results => {
                    this.districts = results.data;
                    if (this.profile.district === 'Private School') {
                        this.district_id = 999999;
                    } else if (this.profile.district_id && this.districts.some(d => d.id === this.profile.district_id)) {
                        this.district_id = this.profile.district_id;
                    } else {
                        this.district_id = 0;
                    }
                    this.loaded = true;
                }).catch(err => {
                    console.error('Professional development profile load failed.', err);
                    this.$notify('error', 'Could Not Load', 'Your profile could not be loaded. Please try again later.', {duration: 6000, permanent: false});
                });
                if (!this.pd) this.$store.dispatch('getDashboard').catch(() => {});
            },
            save() {
                this.processing = true;
                http.put('app/users/pd-profile', {
                    district: this.profile.district,
                    district_id: (this.district_id === 0 || this.district_id === 999999) ? null : this.district_id,
                    school: this.profile.school,
                    school_id: (this.school_id === 0) ? null : this.school_id,
                    meta: this.meta
                }).then(() => {
                    this.processing = false;
                    const updated = {...this.currentUser, district: this.profile.district, school: this.profile.school};
                    localStorage.setItem('user', JSON.stringify(updated));
                    this.setUser(updated);
                    // The dashboard's PD section + salary lane read these
                    this.$store.dispatch('getDashboard').catch(() => {});
                    this.$notify('success', 'Settings Saved', 'Your professional development settings have been saved.', {duration: 5000, permanent: false});
                }).catch(err => {
                    this.processing = false;
                    const message = (err.response && err.response.data) ? err.response.data : 'Something went wrong saving your changes. Please try again.';
                    this.$notify('error', 'Could Not Save', message, {duration: 8000, permanent: false});
                });
            }
        },
        watch: {
            // Same district → school cascade as the profile-verification modal
            district_id(val) {
                this.schools = [];
                if (val === 0) {
                    this.profile.district = 'Not Listed/Not Applicable';
                } else if (val === 999999) {
                    this.profile.district = 'Private School';
                } else {
                    let code;
                    this.districts.forEach(d => {
                        if (d.id === val) {
                            this.profile.district = d.name;
                            code = d.code;
                        }
                    });
                    if (code) {
                        http.get('districts/schoolsIn/' + code).then(results => {
                            this.schools = results.data;
                            if (this.initialSchoolId && this.schools.some(s => s.id === this.initialSchoolId)) {
                                this.school_id = this.initialSchoolId;
                            }
                            this.initialSchoolId = null;
                        }).catch(err => {
                            console.error(err);
                        });
                    }
                }
                if (val === 0 || val === 999999) {
                    this.school_id = 0;
                    this.initialSchoolId = null;
                } else if (!this.initialSchoolId) {
                    this.school_id = undefined;
                }
            },
            school_id(val) {
                if (val === 0) {
                    this.profile.school = 'Not Listed/Not Applicable';
                } else {
                    this.schools.forEach(s => {
                        if (s.id === val) this.profile.school = s.name;
                    });
                }
            }
        },
        mounted() {
            this.load();
        }
    };
</script>

<style lang="scss" scoped>
    .summary-head {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 1rem;
    }

    .stat-strip {
        display: flex;
        flex-wrap: wrap;
        gap: 2rem;

        .stat-value {
            font-size: 1.8rem;
            font-weight: 800;
            line-height: 1.1;
            color: #336699;
        }

        .stat-label {
            font-size: 0.8rem;
            color: #7a7a7a;
        }
    }
</style>
